import React from 'react';
import './CommercialsEditor.css';
import { PRICING_MODELS, CURRENCIES, fmt, calcSummary, ResourceRow, AddlCostRow, BOQRow } from './commercialsUtils';
import useCommercialsEditor from './useCommercialsEditor';
import { formatLocalDateTime } from '../../../../../../utils/date';

const CommercialsEditor = ({ rfpId, commercialsData, onSaveSuccess }) => {
    const {
        state, setState,
        isSaving, lastSaved, saveError,
        updateResource, addResource, deleteResource,
        updateAddlCost, addAddlCost, deleteAddlCost,
        updateBOQItem, addBOQItem, deleteBOQItem,
        updateParam, handleSave
    } = useCommercialsEditor({ rfpId, commercialsData, onSaveSuccess });

    const summary = calcSummary(state.resources, state.additionalCosts, state.params, state.boq);
    const sym = state.currencySymbol || '₹';
    const currCode = state.currencyCode || '';
    const currLabel = currCode || sym;

    return (
        <div className="ce-wrap">

            {/* ── Top header: Commercials title + controls ───────────────────── */}
            <div className="ce-top-header">
                <div className="ce-top-header-left">
                    <div className="ce-title">Commercials</div>
                    <div className="ce-subtitle">Cost estimation &amp; pricing for proposal</div>
                </div>
                <div className="ce-controls-row">
                    <div className="ce-control-item">
                        <label>Model</label>
                        <select value={state.pricingModel} onChange={(e) => setState((prev) => ({ ...prev, pricingModel: e.target.value }))}>
                            {PRICING_MODELS.map((m) => <option key={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="ce-control-item">
                        <label>Currency</label>
                        <select
                            value={state.currencyCode}
                            onChange={(e) => {
                                const selected = CURRENCIES.find((c) => c.code === e.target.value) || CURRENCIES[0];
                                setState((prev) => ({ ...prev, currencyCode: selected.code, currencySymbol: selected.symbol }));
                            }}
                        >
                            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Resource Loading ───────────────────────────────────────────── */}
            <div className="ce-section">
                <div className="ce-sec-head">
                    <h3>Resource Loading</h3>
                    <span className="ce-badge ce-badge-teal">
                        {summary.total_headcount > 0 ? `${summary.total_headcount} resource${summary.total_headcount > 1 ? 's' : ''}` : '0 resources'}
                    </span>
                </div>
                <div className="ce-sec-body ce-table-body">
                    <div className="ce-table-scroll">
                        <table className="ce-table">
                            <colgroup>
                                <col className="col-sl" />
                                <col className="col-resource" />
                                <col className="col-cost" />
                                <col className="col-months" />
                                <col className="col-count" />
                                <col className="col-level" />
                                <col className="col-total" />
                                <col className="col-action" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="th-center">SL NO.</th>
                                    <th>Resource Type</th>
                                    <th className="th-right">Cost / Month ({currLabel})</th>
                                    <th className="th-center">Months</th>
                                    <th className="th-center">Count</th>
                                    <th>Level</th>
                                    <th className="th-center">Total Cost ({currLabel})</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.resources.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="td-empty">
                                            <div className="ce-empty-state">
                                                <div className="ce-empty-icon">
                                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                        <circle cx="12" cy="7" r="4" />
                                                    </svg>
                                                </div>
                                                <div className="ce-empty-title">No resources added yet</div>
                                                <div className="ce-empty-hint">Add resources below to calculate pricing.</div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    state.resources.map((res, idx) => (
                                        <ResourceRow key={res._id} res={res} idx={idx} sym={sym} onUpdate={updateResource} onDelete={deleteResource} />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <button className="ce-add-btn" onClick={addResource}>
                        <span className="ce-add-plus">+</span> Add Resource
                    </button>
                </div>
            </div>

            {/* ── Additional Costs ───────────────────────────────────────────── */}
            <div className="ce-section">
                <div className="ce-sec-head">
                    <h3>Additional Costs</h3>
                    <span className="ce-badge ce-badge-amber">
                        {state.additionalCosts.length > 0 ? `${state.additionalCosts.length} item${state.additionalCosts.length > 1 ? 's' : ''}` : '0 items'}
                    </span>
                </div>
                <div className="ce-sec-body ce-addl-body">
                    {state.additionalCosts.length === 0 ? (
                        <div className="ce-empty-state ce-empty-addl">
                            <div className="ce-empty-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                            </div>
                            <div className="ce-empty-title">No additional costs</div>
                            <div className="ce-empty-hint">Add items such as travel, licensing, or infrastructure.</div>
                        </div>
                    ) : (
                        <div className="ce-addl-table">
                            {/* Addl header row */}
                            <div className="ce-addl-header">
                                <span className="addl-h addl-h-particular">Particular</span>
                                <span className="addl-h addl-h-center">Count</span>
                                <span className="addl-h addl-h-num">Cost / Item ({currLabel})</span>
                                <span className="addl-h addl-h-center">Total Cost ({currLabel})</span>
                                <span className="addl-h"></span>
                            </div>
                            {state.additionalCosts.map((item) => (
                                <AddlCostRow key={item._id} item={item} sym={sym} onUpdate={updateAddlCost} onDelete={deleteAddlCost} />
                            ))}
                        </div>
                    )}
                </div>
                <div className="ce-addl-footer">
                    <button className="ce-add-btn" onClick={addAddlCost}>
                        <span className="ce-add-plus">+</span> Add Cost Item
                    </button>
                </div>
            </div>

            {/* ── Bill of Quantities (BOQ) ───────────────────────────────────── */}
            <div className="ce-section">
                <div className="ce-sec-head">
                    <h3>Bill of Quantities (BOQ)</h3>
                    <span className="ce-badge ce-badge-teal">
                        {(state.boq || []).length > 0 ? `${(state.boq || []).length} item${(state.boq || []).length > 1 ? 's' : ''}` : '0 items'}
                    </span>
                </div>
                <div className="ce-sec-body ce-table-body">
                    <div className="ce-table-scroll">
                        <table className="ce-table">
                            <colgroup>
                                <col style={{ width: '60px' }} />
                                <col style={{ width: '180px' }} />
                                <col style={{ width: '220px' }} />
                                <col style={{ width: '130px' }} />
                                <col style={{ width: '85px' }} />
                                <col style={{ width: '85px' }} />
                                <col style={{ width: '110px' }} />
                                <col style={{ width: '110px' }} />
                                <col style={{ width: '40px' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="th-center">SL NO</th>
                                    <th>ITEM NAME</th>
                                    <th>DESCRIPTION</th>
                                    <th>MODEL</th>
                                    <th className="th-center">QUANTITY</th>
                                    <th className="th-center">UNIT</th>
                                    <th className="th-right">AMOUNT ({currLabel})</th>
                                    <th className="th-right">TOTAL AMOUNT ({currLabel})</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!state.boq || state.boq.length === 0) ? (
                                    <tr>
                                        <td colSpan={9} className="td-empty">
                                            <div className="ce-empty-state">
                                                <div className="ce-empty-icon">
                                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <line x1="16" y1="13" x2="8" y2="13" />
                                                        <line x1="16" y1="17" x2="8" y2="17" />
                                                        <polyline points="10 9 9 9 8 9" />
                                                    </svg>
                                                </div>
                                                <div className="ce-empty-title">No BOQ items added yet</div>
                                                <div className="ce-empty-hint">Add items below to specify quantities and estimated costs.</div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    state.boq.map((item, idx) => (
                                        <BOQRow
                                            key={item._id}
                                            item={item}
                                            idx={idx}
                                            sym={sym}
                                            onUpdate={updateBOQItem}
                                            onDelete={deleteBOQItem}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <button className="ce-add-btn" onClick={addBOQItem}>
                        <span className="ce-add-plus">+</span> Add BOQ Item
                    </button>
                </div>
            </div>

            {/* ── Pricing Parameters ─────────────────────────────────────────── */}
            <div className="ce-section">
                <div className="ce-sec-head">
                    <h3>Pricing Parameters</h3>
                </div>
                <div className="ce-sec-body">
                    <div className="ce-param-grid">
                        {[
                            { key: 'overhead_pct', label: 'Overhead', hint: 'Tools, admin & HR allocation', max: 100 },
                            { key: 'buffer_pct', label: 'Buffer / Contingency', hint: 'Risk, scope creep & attrition', max: 50 },
                            { key: 'profit_margin_pct', label: 'Profit Margin', hint: 'Applied on cost + OH + buffer', max: 100 },
                            { key: 'tax_pct', label: 'VAT / GST', hint: 'Applicable tax (shown separately)', max: 50 },
                        ].map(({ key, label, hint, max }) => (
                            <div className="ce-param-item" key={key}>
                                <span className="ce-param-label">{label}</span>
                                <div className="ce-param-input-wrap">
                                    <input type="number" value={state.params[key]} min="0" max={max} step="0.5" onChange={(e) => updateParam(key, e.target.value)} />
                                    <span className="ce-param-suffix">%</span>
                                </div>
                                <span className="ce-param-hint">{hint}</span>
                            </div>
                        ))}
                    </div>
                    <div className="ce-discount-row">
                        <div className="ce-discount-inline">
                            <span className="ce-param-label">Discount</span>
                            <div className="ce-param-input-wrap" style={{ minWidth: '110px' }}>
                                <input type="number" value={state.params.discount_pct} min="0" max="50" step="0.5" onChange={(e) => updateParam('discount_pct', e.target.value)} />
                                <span className="ce-param-suffix">%</span>
                            </div>
                        </div>
                        <span className="ce-discount-hint">Applied on selling price before tax</span>
                    </div>
                </div>
            </div>

            {/* ── Pricing Summary ────────────────────────────────────────────── */}
            <div className="ce-section">
                <div className="ce-sec-head">
                    <h3>Pricing Summary</h3>
                </div>
                <div className="ce-sec-body">
                    <div className="ce-summary-grid">
                        <div className="ce-s-card">
                            <div className="ce-s-label">Base Cost</div>
                            <div className="ce-s-val">{(summary.base_resource_cost + summary.total_additional_cost + (summary.total_boq_cost || 0)) > 0 ? fmt(summary.base_resource_cost + summary.total_additional_cost + (summary.total_boq_cost || 0), sym) : <span className="ce-s-empty">—</span>}</div>
                        </div>
                        <div className="ce-s-card">
                            <div className="ce-s-label">Selling Price (pre-tax)</div>
                            <div className="ce-s-val">{summary.selling_price_pre_tax > 0 ? fmt(summary.selling_price_pre_tax, sym) : <span className="ce-s-empty">—</span>}</div>
                        </div>
                        <div className="ce-s-card ce-s-card-hi">
                            <div className="ce-s-label">Grand Total (incl. tax)</div>
                            <div className="ce-s-val">{summary.grand_total > 0 ? fmt(summary.grand_total, sym) : <span className="ce-s-empty">—</span>}</div>
                        </div>
                    </div>

                    <div className="ce-breakdown">
                        <div className="ce-brow"><span className="lbl">Resource Cost</span><span className="amt">{summary.base_resource_cost > 0 ? fmt(summary.base_resource_cost, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">Additional Costs</span><span className="amt">{summary.total_additional_cost > 0 ? fmt(summary.total_additional_cost, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">BOQ Cost</span><span className="amt">{summary.total_boq_cost > 0 ? fmt(summary.total_boq_cost, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">Overhead ({state.params.overhead_pct}%)</span><span className="amt">{summary.total_overhead > 0 ? fmt(summary.total_overhead, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">Buffer ({state.params.buffer_pct}%)</span><span className="amt">{summary.total_buffer > 0 ? fmt(summary.total_buffer, sym) : '—'}</span></div>
                        <div className="ce-brow sub"><span className="lbl">Total Cost</span><span className="amt">{summary.total_cost > 0 ? fmt(summary.total_cost, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">Profit Margin ({state.params.profit_margin_pct}%)</span><span className="amt">{summary.profit_margin_amt > 0 ? fmt(summary.profit_margin_amt, sym) : '—'}</span></div>
                        {state.params.discount_pct > 0 && (
                            <div className="ce-brow"><span className="lbl">Discount ({state.params.discount_pct}%)</span><span className="amt" style={{ color: '#EF4444' }}>{summary.discount_amount > 0 ? `−${fmt(summary.discount_amount, sym)}` : '—'}</span></div>
                        )}
                        <div className="ce-brow sub"><span className="lbl">Selling Price</span><span className="amt">{summary.selling_price_pre_tax > 0 ? fmt(summary.selling_price_pre_tax, sym) : '—'}</span></div>
                        <div className="ce-brow"><span className="lbl">VAT / GST ({state.params.tax_pct}%)</span><span className="amt">{summary.tax_amount > 0 ? fmt(summary.tax_amount, sym) : '—'}</span></div>
                        <div className="ce-brow grand"><span className="lbl">Grand Total</span><span className="amt">{summary.grand_total > 0 ? fmt(summary.grand_total, sym) : '—'}</span></div>
                    </div>
                </div>
            </div>

            {/* ── Save bar ───────────────────────────────────────────────────── */}
            <div className="ce-save-bar">
                {saveError && <span className="ce-save-error">{saveError}</span>}
                {lastSaved && !saveError && <span className="ce-saving-indicator">✓ Saved at {formatLocalDateTime(lastSaved)}</span>}
                <button className="ce-save-btn" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <><span className="ce-spinner"></span>Saving…</> : 'Save Commercials'}
                </button>
            </div>
        </div>
    );
};

export default CommercialsEditor;
